// Tiny synchronous event bus for rocketry mode.
//
// Gameplay code announces what happened and the presentation layers (audio,
// VFX, HUD callouts) subscribe, so neither side has to know about the other.
// Handlers run immediately, in subscription order; one that throws is logged
// and skipped so a broken effect can never stall the simulation.
//
// Events emitted by index.js (payload fields in braces):
//   mode       { mode, prev }                  KSC / VAB / flight switch
//   map        { on }                          map view toggled
//   launch     { vessel }                      a design was rolled out to the pad (it
//                                              stands in the clamps: vessel.clamped)
//   countdown  { vessel, t }                   each whole second of the terminal count,
//                                              t = -10 (or -3) ... 0 (launch.js)
//   ignition   { vessel, part }                an engine lit: at T-3 for liquids, T-0 for
//                                              solids, and on every staging in flight.
//                                              part.spool (0..1) then climbs over 1 to 3 s
//   clamps     { vessel }                      hold-down clamps released at T-0
//   hold       { vessel, reason, thrust, weight }  the count stopped with the clamps on:
//                                              'throttle' (waiting for the pilot to throttle
//                                              up), 'underpowered' (scrubbed, engines shut
//                                              down), 'abort' (left flight mid-count)
//   stage      { vessel, parts, spawned, events }  space bar: parts = the stage's part records
//                                              (not emitted when Space starts the count)
//   liftoff    { vessel }                      clamps released, vessel left the ground
//   maxq       { vessel, q }                   the ascent just passed its peak dynamic
//                                              pressure q (Pa), once per flight
//   separation { vessel, debris }              one piece came off (decoupler or SRB)
//   burnout    { vessel, part }                a solid ran dry
//   flameout   { vessel, part }                a running liquid engine ran out of propellant
//   chute      { vessel, phase, part }         'deployed' (opens reefed), 'full' (disreefing
//                                              near 1200 m), 'failed' (opened or loaded past
//                                              10 kPa / 600 m/s and torn away), 'cut'
//                                              (released on touchdown). part.chuteOpen is
//                                              the opened fraction, part.failed a torn canopy
//   overheat   { vessel, part, spawned, temp } a part passed its temperature limit and
//                                              burned off; spawned = pieces that fell away
//   destroyed  { vessel, cause }               the whole vessel is gone: 'heat' (burned up)
//                                              or 'impact' (after crash). Fired before removal
//   landed     { vessel, body, splashed, speed }  touchdown, on water when splashed
//   splashdown { vessel, speed }               touchdown on water (after landed)
//   crash      { vessel, speed }               fired before the vessel is removed
//   soi        { vessel, body }                entered a new sphere of influence
//   orbit      { vessel }                      first stable orbit of this flight
//   dockcapture { vessel, other }               two ports took hold; the latch draws
//                                              them together over a second or two
//                                              (this and the three below: dockaids.js)
//   docked     { vessel, other }               latched and merged: other is now part
//                                              of vessel (and no longer in R.vessels)
//   dockbump   { vessel, speed, angle, reason } ports touched outside the latch window
//                                              and bounced: 'fast', 'angle' or 'offset'
//   undocked   { vessel, other }               other is the vessel that left
//   panels     { vessel, deployed }            solar arrays and antennas opened or stowed
//   recovered  { vessel, refund }
//   contract   { contract }
//   warp       { index, rate }
//   revert     { to }                          'launch' or 'vab' (G.revertToLaunch / G.revertToVAB)
//   quicksave  {}                              F5 wrote the quicksave slot
//   quickload  {}                              F9 restored it
//   enter      {}                              rocketry mode opened (after setup)
//   exit       {}                              rocketry mode closing
//   frame      { realDt, dt, now }             once per rendered frame, after the
//                                              scene is placed and before the HUD
//
// State others can poll every frame rather than wait for an event: on the
// vessel, gForce (sensed g: 1 on the pad, 0 coasting), mach, dynamicPressure
// (Pa), heatFlux (W/m2), skinTemp (K), heatRatio (0..1+ of the hottest
// part's limit), heatShield (ablator left 0..1, or null), heatShieldActive,
// chuteSafe, clamped, countdown ({ t } while counting), splashed; on each
// engine part, spool, ignitionTime, flameout, currentThrust (N); on each part,
// temp and, for pods, shieldTemp (K) and ablated (kg).

export class EventBus {
  constructor() {
    this.handlers = new Map();
  }

  // Returns an unsubscribe function.
  on(name, fn) {
    let list = this.handlers.get(name);
    if (!list) { list = []; this.handlers.set(name, list); }
    list.push(fn);
    return () => this.off(name, fn);
  }

  off(name, fn) {
    const list = this.handlers.get(name);
    if (!list) return;
    const i = list.indexOf(fn);
    if (i >= 0) list.splice(i, 1);
  }

  emit(name, payload = {}) {
    const list = this.handlers.get(name);
    if (!list || !list.length) return;
    for (const fn of [...list]) {
      try { fn(payload); } catch (err) { console.error(`[rocketry] ${name} handler failed`, err); }
    }
  }
}
